import React, { useContext, useEffect, useState } from 'react';
import UserHistoryContext from '../../contexts/data/users/UserHistoryContext';
import { Table, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import {
    historyEntryNameAsString,
    historyEntryTypeAsString,
} from '@leedpulse-monorepo/enums';
import UserDoc from '../../../domain/models/UserDoc';
import UserHistory from '../../../domain/models/user_doc/UserHistory';
import UserHistoryDetails from './user_history/UserHistoryDetails';

interface UserHistoryListProps {
    user: UserDoc;
}

export default function UserHistoryList({ user }: UserHistoryListProps) {
    const [currentPage, setCurrentPage] = useState(1);
    const userHistoryContext = useContext(UserHistoryContext);

    const handlePaginationChange = (page: number) => {
        setCurrentPage(page);
        const currentSlice = userHistoryContext.items.slice(
            (page - 1) * userHistoryContext.hitsPerPage,
            page * userHistoryContext.hitsPerPage
        );
        if (currentSlice.length === 0 && userHistoryContext.items.length < userHistoryContext.count) {
            userHistoryContext.fetchNext();
        }
    };

    const handlePageSizeChange = (_current: number, newPageSize: number) => {
        setCurrentPage(1);
        userHistoryContext.fetch(user.uid, newPageSize);
    };

    const handleRefresh = () => {
        setCurrentPage(1);
        userHistoryContext.fetch(user.uid);
    };

    useEffect(() => {
        if (
            userHistoryContext.status === ContextStatus.IDLE ||
            userHistoryContext.scopedUserUid !== user.uid
        ) {
            userHistoryContext.fetch(user.uid);
        }
        return () => {
            userHistoryContext.reset();
        };
    }, [user.uid]);

    const columns = [
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            render: (type: UserHistory['type']) => historyEntryTypeAsString(type),
        },
        {
            title: 'Événement',
            dataIndex: 'name',
            key: 'name',
            render: (name: UserHistory['name']) => historyEntryNameAsString(name),
        },
        {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (timestamp: number) => moment(timestamp).format('DD/MM/YYYY à HH:mm'),
        },
        {
            title: 'Détails',
            key: 'details',
            render: (record: UserHistory) => <UserHistoryDetails userHistory={record} />,
        },
    ];

    return (
        <div className="list-container">
            <div className="list-container__controls">
                <h2>Historique des actions</h2>
                <Button type="primary" onClick={handleRefresh} icon={<ReloadOutlined />}>
                    Actualiser
                </Button>
            </div>
            <Table
                pagination={{
                    current: currentPage,
                    total: userHistoryContext.count,
                    pageSize: userHistoryContext.hitsPerPage,
                    onChange: handlePaginationChange,
                    onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
                    showSizeChanger: true,
            simple: true,
                    pageSizeOptions: [20, 50, 100, 200],
                    position: ['bottomRight', 'topRight'],
                    disabled: userHistoryContext.status === ContextStatus.LOADING,
                }}
                loading={userHistoryContext.status === ContextStatus.LOADING}
                columns={columns}
                rowKey={(record) => String(record.timestamp)}
                dataSource={userHistoryContext.items.slice(
                    (currentPage - 1) * userHistoryContext.hitsPerPage,
                    currentPage * userHistoryContext.hitsPerPage
                )}
            />
        </div>
    );
}
